/**
 * Parsers for voice-form fields.
 *
 * Each parser takes a raw transcript string and returns a clean value
 * for its target field. They are pure functions — no side effects, no
 * DOM access — so they can be unit-tested without a browser.
 *
 * NOTE: The Web Speech API returns number-words as digits for all
 * supported Indic languages, so the same numeric parsers work across
 * every language.
 */

/** Pulls the first number from a string. Returns '' if none found. */
export function extractNumber(text, { integer = false } = {}) {
  const m = String(text).match(/-?\d+(\.\d+)?/);
  if (!m) return '';
  const n = parseFloat(m[0]);
  return integer ? String(Math.round(n)) : String(n);
}

/** Strips filler phrases from a name transcript (English + Indic). */
export function cleanName(t) {
  const prefixes = /^(my name is|i am|i'm|this is|call me|mera naam|मेरा नाम|mera name|माझं नाव|mazha naav|mazha nav|माझे नाव|મારું નામ|maru naam|माझे नांव)\s+/i;
  return String(t).trim()
    .replace(prefixes, '')
    .replace(/[.,!?]+$/, '')
    .replace(/\s+/g, ' ');
}

/** Extracts a clamped integer age from a transcript. */
export function parseAge(text) {
  const n = extractNumber(text, { integer: true });
  if (!n) return '';
  return String(Math.max(0, Math.min(130, parseInt(n, 10))));
}

/** Extracts weight in kg, converting from pounds if "lb/lbs/pound(s)" was said. */
export function parseWeight(text) {
  const isPounds = /\b(pound|pounds|lb|lbs)\b/i.test(text);
  const n = extractNumber(text);
  if (!n) return '';
  let kg = parseFloat(n);
  if (isPounds) kg = kg * 0.45359237;
  return kg.toFixed(1);
}

/** Tidies up a spoken address into a more presentable form. */
export function cleanAddress(t) {
  return String(t).trim()
    // Handle spoken punctuation FIRST (so subsequent regexes can normalize)
    .replace(/\s+new line\b/gi, ', ')
    .replace(/\bcomma\b/gi, ',')
    .replace(/\bperiod\b|\bdot\b|\bfull stop\b/gi, '.')
    .replace(/\bapartment\b/gi, 'Apt.')
    // Then normalize whitespace and punctuation spacing
    .replace(/\s+/g, ' ')
    .replace(/\s+,/g, ',')
    .replace(/,\s+/g, ', ')
    .replace(/\s+\./g, '.')
    .replace(/\s+([.,])/g, '$1')
    .replace(/[.,!?]+$/, '')
    .trim();
}

/** Pulls a digits-only string from a transcript (for phone / pin / id numbers). */
export function digitsOnly(text) {
  const m = String(text).replace(/\D+/g, '');
  return m;
}
